/* eslint-disable complexity */
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import ua from '@tencent/docs-user-agent';
import _isBoolean from 'lodash/isBoolean';
import { MemoryClipboard, SlideLegacyClipboard, TencentClipboard } from '../clipboard-libs';
import {
    ClipboardDevice,
    IClipboardDeviceDelegate,
    IClipboardDeviceDelegateInjectable,
    ReadOptions,
    ReadParamsMap,
    ReadResult,
    WriteOptions,
    WriteParamsMap,
} from './clipboard-delegate.interface';

/**
 * 剪切板被委任类
 * 提供给上层统一的接口调用 read write
 * 调用底层 MemoryClipboard SlideLegacyClipboard TencentClipboard
 **/
export class ClipboardDeviceDelegate implements IClipboardDeviceDelegate {
    private memoryClipboard: MemoryClipboard;
    private slideLegacyClipboard: SlideLegacyClipboard;
    private tencentClipboard: TencentClipboard;
    public constructor(private clipboardDeviceDelegateInjectable: IClipboardDeviceDelegateInjectable) {
        this.memoryClipboard = MemoryClipboard.getInstance();
        this.slideLegacyClipboard = SlideLegacyClipboard.getInstance();
        this.tencentClipboard = TencentClipboard.getInstance();
    }

    public async write(args?: { params?: WriteParamsMap; options?: WriteOptions }): Promise<void> {
        try {
            const { useMemoryClipboardExtra, useMemoryClipboard, clipboardDevice } = args?.options || {};
            // 是否需要额外写到内存剪切板中
            if (useMemoryClipboardExtra) {
                const { dataMap } = args?.params?.memory || {};
                this.memoryClipboard.write(dataMap);
            }

            // 是否只写内存剪切板
            if (useMemoryClipboard) {
                return new Promise((resolve) => {
                    try {
                        const { dataMap } = args?.params?.memory || {};
                        this.memoryClipboard.write(dataMap);
                        resolve();
                    } catch (error) {
                        window.log.error('write memory clipboard error', error);
                        resolve();
                    }
                });
            }

            const adapterParams = args?.params?.adapter || {};

            window.log.info(
                '[ClipboardDeviceDelegate] write info:',
                `useMemoryClipboardExtra: ${!!useMemoryClipboardExtra}`,
                `useMemoryClipboard: ${!!useMemoryClipboard}`,
                `clipboardDevice: ${clipboardDevice ?? this.automaticallySelectClipboardDevice}`,
            );

            switch (clipboardDevice ?? this.automaticallySelectClipboardDevice) {
                case ClipboardDevice.SlideLegacyClipboard: {
                    return this.slideLegacyClipboard.write({
                        adapter: adapterParams,
                        legacy: args?.params?.legacy,
                    });
                }
                case ClipboardDevice.TencentClipboard: {
                    const canCopyOut = SlideDataServices.PrivilegeManager()?.getPrivilegeAttribute().can_copy_out;
                    const needEncrypt = _isBoolean(canCopyOut) ? !canCopyOut : false;

                    // 加密后，如果有复制操作，需要弹出相关提示
                    if (needEncrypt) {
                        this.clipboardDeviceDelegateInjectable.showClipboardProtection?.();
                    }

                    return this.tencentClipboard.write(adapterParams, {
                        encrypt: needEncrypt,
                        encryptKey: SlideDataServices.ClientVarsService()?.getClientVars().creatorCorpId,
                    });
                }
                default:
                    throw Error('No matching clipboard found');
            }
        } catch (error) {
            window.log.error('Failed to write to clipboard: ', error);
            return Promise.resolve();
        }
    }

    public async read<T extends ReadResult>(args?: {
        params?: ReadParamsMap;
        options?: ReadOptions;
    }): Promise<T | undefined> {
        try {
            const { useMemoryClipboard, clipboardDevice } = args?.options || {};

            // 是否只读内存剪切板
            // 如果没有读取到内容，尝试读取系统剪切板中的内容
            if (useMemoryClipboard) {
                try {
                    const data = this.memoryClipboard.read() ?? {};
                    if (Object.keys(data).length) {
                        return Promise.resolve(data);
                    }
                } catch (error) {
                    window.log.error('read memory clipboard error', error);
                }
            }

            const adapterParams = args?.params?.adapter || {};

            window.log.info(
                '[ClipboardDeviceDelegate] read info:',
                `useMemoryClipboard: ${!!useMemoryClipboard}`,
                `clipboardDevice: ${clipboardDevice ?? this.automaticallySelectClipboardDevice}`,
            );

            switch (clipboardDevice ?? this.automaticallySelectClipboardDevice) {
                case ClipboardDevice.SlideLegacyClipboard: {
                    return this.slideLegacyClipboard.read({
                        adapter: adapterParams,
                        legacy: args?.params?.legacy,
                    }) as T;
                }
                case ClipboardDevice.TencentClipboard: {
                    return this.tencentClipboard.read({
                        ...adapterParams,
                        encryptKey: SlideDataServices.ClientVarsService()?.getClientVars().creatorCorpId,
                    }) as Promise<T>;
                }
                default:
                    throw Error('No matching clipboard found');
            }
        } catch (error) {
            window.log.error('Failed to read from clipboard: ', error);
            return Promise.resolve(undefined);
        }
    }

    // 自动选择剪切板设备
    private get automaticallySelectClipboardDevice() {
        // 私有化环境下，直接使用 TencentClipboard
        if (this.clipboardDeviceDelegateInjectable.currentEnvIsInPrivate) {
            return ClipboardDevice.TencentClipboard;
        }
        // 非私有化环境下，部分情况下使用 TencentClipboard
        if (ua.isMobile) {
            return ClipboardDevice.TencentClipboard;
        }

        return ClipboardDevice.SlideLegacyClipboard;
    }
}
